import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import {
  ArrowLeft,
  ArrowRight,
  Download,
  LogIn,
  LogOut,
  Menu,
  Pencil,
  Settings,
  Shield,
  Trash2,
  Users,
  Eye,
  X,
} from "lucide-react";
import { api } from "./api";
import { useApp } from "./context";
import type { Character, Media } from "./types";

export function Layout({ children }: { children: ReactNode }) {
  const { content, user, refreshUser } = useApp();
  const [open, setOpen] = useState(false);
  const [location, navigate] = useLocation();

  useEffect(() => setOpen(false), [location]);

  async function logout() {
    await api("/auth/logout", { method: "POST" });
    await refreshUser();
    navigate("/");
  }

  return (
    <>
      <header className="topbar">
        <Link href="/" className="brand" aria-label="Domů">
          {content?.settings.logo_image ? (
            <img src={content.settings.logo_image} alt={content.settings.site_title || "FG"} />
          ) : (
            <span className="brand-copy">
              <strong>{content?.settings.site_title === "FG" ? "Family Game" : content?.settings.site_title || "Family Game"}</strong>
              <small>Psaný seriál</small>
            </span>
          )}
        </Link>
        <button className="nav-toggle" onClick={() => setOpen(!open)} aria-label="Otevřít menu">
          {open ? <X /> : <Menu />}
        </button>
        <nav className={open ? "nav open" : "nav"}>
          <NavItem href="/postavy" current={location}>Postavy</NavItem>
          <NavItem href="/mesto" current={location}>Město</NavItem>
          <NavItem href="/plot-prog" current={location}>Plot&amp;Prog</NavItem>
          <span className="nav-divider" />
          {user && user.role !== "reader" && (
            <Link href="/admin" className={`nav-icon-link${location === "/admin" ? " active" : ""}`}>
              <Settings size={17} /> Správa
            </Link>
          )}
          {user ? (
            <button className="nav-button" onClick={logout}>
              <span className={`role-badge ${user.role}`}>
                {user.role === "admin" ? <Shield size={13} /> : user.role === "collaborator" ? <Users size={13} /> : <Eye size={13} />}
                {user.role === "admin" ? "Admin" : user.role === "collaborator" ? "Spolupracovník" : "Čtenář"}
              </span>
              <LogOut size={17} />
            </button>
          ) : (
            <Link href="/prihlaseni" className={`nav-icon-link${location === "/prihlaseni" ? " active" : ""}`}>
              <LogIn size={17} /> Přihlásit
            </Link>
          )}
        </nav>
      </header>
      <main>{children}</main>
      <footer>
        <Link href="/" className="footer-brand" aria-label="Domů">
          {content?.settings.logo_image ? (
            <img src={content.settings.logo_image} alt={content.settings.site_title || "Family Game"} />
          ) : (
            content?.settings.site_title === "FG" ? "Family Game" : content?.settings.site_title || "Family Game"
          )}
        </Link>
        <p>Příběhový svět. Všechna práva vyhrazena.</p>
        <Link href="/prihlaseni">{user ? user.display_name : "Přihlášení"}</Link>
      </footer>
    </>
  );
}

function NavItem({ href, current, children }: { href: string; current: string; children: ReactNode }) {
  const active = current === href || current.startsWith(`${href}/`);
  return <Link href={href} className={active ? "active" : ""}>{children}</Link>;
}

export function Loader() {
  return (
    <div className="loader-wrap">
      <span className="loader" />
    </div>
  );
}

export function PageIntro({
  eyebrow,
  title,
  text,
  action,
}: {
  eyebrow: string;
  title: string;
  text?: string;
  action?: ReactNode;
}) {
  return (
    <section className="page-intro">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        {text && <p className="intro-text">{text}</p>}
      </div>
      {action}
    </section>
  );
}

export function CharacterCard({ character, index = 0 }: { character: Character; index?: number }) {
  return (
    <Link
      href={`/postavy/${character.slug}`}
      className="character-card reveal"
      style={{ animationDelay: `${Math.min(index * 45, 450)}ms` }}
    >
      <div className="card-copy">
        <h3>
          <strong>{character.first_name}</strong>
          <span>{character.last_name}</span>
        </h3>
        <span className="hairline" />
        <p>{character.job_title || "story character"}</p>
        <span className="info-pill">info</span>
      </div>
      <img src={character.card_image || character.main_image} alt={`${character.first_name} ${character.last_name}`} />
      <span className="card-arrow"><ArrowRight size={18} /></span>
    </Link>
  );
}

export function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  useEffect(() => {
    const listener = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, [onClose]);
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="modal" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="icon-button" onClick={onClose} aria-label="Zavřít"><X /></button>
        </div>
        {children}
      </section>
    </div>
  );
}

export function ConfirmDialog({
  title,
  text,
  onCancel,
  onConfirm,
}: {
  title: string;
  text: string;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  return (
    <Modal title={title} onClose={onCancel}>
      <p className="modal-text">{text}</p>
      <div className="form-actions">
        <button className="button ghost" onClick={onCancel}>Zrušit</button>
        <button className="button danger" onClick={onConfirm}><Trash2 size={17} /> Smazat definitivně</button>
      </div>
    </Modal>
  );
}

export function MediaCarousel({
  title,
  items,
  empty,
  variant = "grid",
}: {
  title: string;
  items: Media[];
  empty: string;
  variant?: "garage" | "grid";
}) {
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Media | null>(null);
  const pageCount = variant === "garage" ? items.length : Math.ceil(items.length / 3);
  const move = (direction: number) => {
    if (!pageCount) return;
    setPage((current) => (current + direction + pageCount) % pageCount);
  };
  const visible = variant === "garage" ? items.slice(page, page + 1) : items.slice(page * 3, page * 3 + 3);

  useEffect(() => {
    if (page >= pageCount) setPage(0);
  }, [page, pageCount]);

  useEffect(() => {
    if (!selected) return;
    const close = (event: KeyboardEvent) => event.key === "Escape" && setSelected(null);
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [selected]);

  return (
    <>
      <section className={`media-section ${variant === "garage" ? "garage-section" : "gallery-section"}`}>
        <div className="section-heading">
          <div>
            <p className="eyebrow">Archiv</p>
            <h2>{title}</h2>
          </div>
          {pageCount > 1 && (
            <div className="carousel-controls">
              <button className="icon-button light" onClick={() => move(-1)} aria-label="Předchozí">
                <ArrowLeft />
              </button>
              <button className="icon-button light" onClick={() => move(1)} aria-label="Další">
                <ArrowRight />
              </button>
            </div>
          )}
        </div>
        {items.length ? (
          <div className={variant === "garage" ? "garage-banner" : "media-thumbnail-grid"}>
            {visible.map((item) => (
              <button key={item.id} type="button" onClick={() => setSelected(item)} aria-label={`Zvětšit fotografii – ${title}`}>
                <img src={item.url} alt={item.alt || title} loading="lazy" />
              </button>
            ))}
          </div>
        ) : (
          <div className="empty-media">{empty}</div>
        )}
      </section>
      {selected && (
        <div className="media-lightbox" role="presentation" onMouseDown={() => setSelected(null)}>
          <div role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
            <img src={selected.url} alt={selected.alt || title} />
            <button onClick={() => setSelected(null)} aria-label="Zavřít zvětšenou fotografii"><X /></button>
          </div>
        </div>
      )}
    </>
  );
}

export function DocumentButton({ url, title, fileName }: { url: string; title: string; fileName: string }) {
  return (
    <a className="document-button" href={url} download={fileName}>
      <span className="document-index">FILE</span>
      <span>
        <strong>{title}</strong>
        <small>{fileName}</small>
      </span>
      <Download />
    </a>
  );
}

export function EditButton({ to, label = "Upravit" }: { to: string; label?: string }) {
  return <Link className="button dark" href={to}><Pencil size={17} /> {label}</Link>;
}
